import { Injectable } from '@angular/core'
import {
  Observable,
  combineLatest,
  concatMap,
  from,
  last,
  map,
  of,
  scan,
  switchMap,
} from 'rxjs'
import { MovementsService } from './movements.service'
import { Page } from '../../models/util.model'
import { Reason, ReasonsService } from '../reasons'
import { uniqBy } from 'lodash'
import {
  MovementField,
  MovementSearchParams,
} from './models/movement-search.model'
import {
  MovementsListingData,
  MovementsListingPage,
} from './models/movement-listing.model'
import { Movement } from './models/movement.entity'
import { GoodsReceive, GoodsReceivesService } from '../goods-receives'
import { User, UsersService } from '../users'
import { Warehouse, WarehousesService } from '../warehouses'
import { parseMovementPageKeys } from './libs/movements-page.lib'

@Injectable({
  providedIn: 'root',
})
export class MovementsRepository {
  constructor(
    private movementsService: MovementsService,
    private goodsReceivesService: GoodsReceivesService,
    private reasonsService: ReasonsService,
    private usersService: UsersService,
    private warehousesService: WarehousesService,
  ) {}

  /**
   * Search movements and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search movements
   */
  searchMovements$(
    searchParams: MovementSearchParams,
    fields: MovementField[],
    listingData?: MovementsListingData,
    returnAll?: boolean,
  ): Observable<MovementsListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.movementsService.limit

    return needsPagination
      ? this._paginateMovements$(searchParams, fields, listingData)
      : this._searchMovements$(searchParams, fields, listingData, returnAll)
  }

  /**
   * Search goods returns with relative external data
   * @param searchParams - the search params
   * @param fields - the movements fields
   * @param listingData - the already loaded listing data
   * @returns the observable<MovementsListingPage> for search movements
   */
  private _searchMovements$(
    searchParams: MovementSearchParams,
    fields: MovementField[],
    listingData?: MovementsListingData,
    returnAll?: boolean,
  ): Observable<MovementsListingPage> {
    return this.movementsService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, fields, listingData).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Load external page movements data
   * @param page - the movements page
   * @param fields - the movements fields
   * @returns the observable for load external data
   */
  private _loadExtData$(
    page: Page<Movement>,
    fields: MovementField[],
    listingData?: MovementsListingData,
  ): Observable<MovementsListingData> {
    const extDataKeys = parseMovementPageKeys(page)
    const obs$: Partial<{
      goodsReceives: Observable<GoodsReceive[]>
      reasons: Observable<Reason[]>
      users: Observable<User[]>
      warehouses: Observable<Warehouse[]>
    }> = {}

    if (
      fields.includes('goodsReceiveRef') &&
      extDataKeys.goodsReceivesRefs?.length
    ) {
      obs$.goodsReceives = this.goodsReceivesService.list$(
        {
          'header.orderNumber': extDataKeys.goodsReceivesRefs,
        },
        true,
      )
    }

    if (fields.includes('reason')) {
      obs$.reasons = this.reasonsService.list$(undefined, true)
    }

    if (extDataKeys.userIds && fields.includes('userId')) {
      const existingUsers = listingData?.users || []
      const hasAllUsers = extDataKeys.userIds.every((id) =>
        existingUsers.some((user) => user._id === id),
      )

      if (!hasAllUsers) {
        obs$.users = this.usersService.store$(
          extDataKeys.userIds,
          existingUsers,
        )
      } else {
        obs$.users = of(existingUsers)
      }
    }

    if (extDataKeys.warehouseIds && fields.includes('warehouseId')) {
      const existingWarehouses = listingData?.warehouses || []
      const hasAllWarehouses = extDataKeys.warehouseIds.every((id) =>
        existingWarehouses.some((wh) => wh._id === id),
      )

      if (!hasAllWarehouses) {
        obs$.warehouses = this.warehousesService.store$(
          extDataKeys.warehouseIds,
          existingWarehouses,
        )
      } else {
        obs$.warehouses = of(existingWarehouses)
      }
    }

    if (!Object.keys(obs$).length) {
      return of({} as MovementsListingData)
    }

    return combineLatest(obs$).pipe(
      map((results) => {
        const extData: MovementsListingData = {}
        if (results.goodsReceives) extData.goodsReceives = results.goodsReceives
        if (results.reasons) extData.reasons = results.reasons
        if (results.users) extData.users = results.users
        if (results.warehouses) extData.warehouses = results.warehouses
        return extData
      }),
    )
  }

  /**
   * Search more than 200 movements with relative external data
   * @param searchParams - the search params
   * @param fields - the movements fields
   * @param listingData - the already loaded listing data
   * @returns the observable<MovementsListingPage> for search movements
   */
  private _paginateMovements$(
    searchParams: MovementSearchParams,
    fields: MovementField[],
    listingData?: MovementsListingData,
  ): Observable<MovementsListingPage> {
    const totalCount = searchParams.limit || this.movementsService.limit
    const pagesCount = Math.ceil(totalCount / this.movementsService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.movementsService.limit,
        offset: i * this.movementsService.limit,
      })),
    ).pipe(
      concatMap((pageParams) =>
        this._searchMovements$(pageParams, fields, listingData),
      ),
      scan<MovementsListingPage, MovementsListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as MovementsListingPage,
      ),
      last(),
    )
  }
}
